import React, { useMemo } from 'react';

import { Button, CircularProgress, Divider } from '@mui/material';
import PropTypes from 'prop-types';

import { resolveColor } from 'constants/colors';
import { getIconComponentByValue } from 'constants/iconOptions';
import { ItemGroupAlignment } from 'constants/ui';
import { getColorStyles } from 'utils/colorStyles';

import { RUNNING_INDICATOR_SIZE } from './ActionButton.constants';
import { dividerStyles, getButtonStyles } from './ActionButton.styles';

const ActionButton = ({
  children,
  onClick,
  disabled = false,
  dataId,
  dataTestId = null,
  name,
  customStyles,
  withDividerBefore = false,
  withDividerAfter = false,
  dividerOrientation = ItemGroupAlignment.VERTICAL,
  icon = null,
  color = null,
  variant = null,
  isCompact = true,
  isRunning = false,
}) => {
  const IconComponent = useMemo(() => getIconComponentByValue(icon), [icon]);

  const startIcon = useMemo(() => {
    if (isRunning) {
      return <CircularProgress color="inherit" size={RUNNING_INDICATOR_SIZE} />;
    }

    return IconComponent ? <IconComponent /> : null;
  }, [isRunning, IconComponent]);

  const resolvedColor = useMemo(() => resolveColor(color), [color]);

  const colorStyles = useMemo(
    () => getColorStyles({ resolvedColor, variant }),
    [resolvedColor, variant],
  );

  const buttonVariant = variant || 'outlined';

  return (
    <>
      {withDividerBefore && <Divider orientation={dividerOrientation} sx={dividerStyles} />}
      <Button
        aria-busy={isRunning}
        color={resolvedColor ? 'inherit' : 'primary'}
        data-id={dataId}
        data-testid={dataTestId}
        disabled={disabled}
        name={name}
        startIcon={startIcon}
        sx={getButtonStyles({ customStyles, colorStyles, variant: buttonVariant, isCompact })}
        variant={buttonVariant}
        onClick={onClick}
      >
        {children}
      </Button>
      {withDividerAfter && <Divider orientation={dividerOrientation} sx={dividerStyles} />}
    </>
  );
};

ActionButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  color: PropTypes.string,
  customStyles: PropTypes.object,
  dataId: PropTypes.string,
  dataTestId: PropTypes.string,
  disabled: PropTypes.bool,
  dividerOrientation: PropTypes.oneOf(Object.values(ItemGroupAlignment)),
  icon: PropTypes.string,
  isCompact: PropTypes.bool,
  isRunning: PropTypes.bool,
  name: PropTypes.string,
  variant: PropTypes.string,
  withDividerAfter: PropTypes.bool,
  withDividerBefore: PropTypes.bool,
};

export default ActionButton;
